import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Button,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Pagination,
  Stack,
  Autocomplete,
} from '@mui/material';
import { Upload, Edit, Delete, Add, Construction } from '@mui/icons-material';
import FileImport from './FileImport';
import { useAuth } from '../contexts/AuthContext';
import { useTransactions, useAvailableFiscalYears } from '../hooks/useTransactions';
import { supabase } from '../lib/supabase';
import type { AccountType, TransactionType, Transaction } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { TransactionIngestionService, normalizeManualInput } from '../services/transactionIngestion';
import { useCategories } from '../hooks/useCategories';

// 将来の実装用（現在はコメントアウト）
/*
interface ImportHistory {
  id: string;
  fileName: string;
  importDate: string;
  recordCount: number;
  status: 'success' | 'error' | 'warning';
  accountType: 'general' | 'reserve';
}
*/

// 将来の実装用（現在はコメントアウト）
/*
const mockImportHistory: ImportHistory[] = [
  { id: '1', fileName: 'YYYY年M月一般会計.xlsx', importDate: 'YYYY-MM-DD', recordCount: 25, status: 'success', accountType: 'general' },
  { id: '2', fileName: 'YYYY年M月積立会計.xlsx', importDate: 'YYYY-MM-DD', recordCount: 3, status: 'success', accountType: 'reserve' },
  { id: '3', fileName: 'YYYY年M月一般会計.pdf', importDate: 'YYYY-MM-DD', recordCount: 0, status: 'error', accountType: 'general' },
  { id: '4', fileName: 'YYYY年M月積立会計.xlsx', importDate: 'YYYY-MM-DD', recordCount: 4, status: 'warning', accountType: 'reserve' },
];
*/

interface TransactionForm {
  account_type: AccountType;
  type: TransactionType;
  amount: number;
  category: string;
  transaction_date: string;
  description?: string;
  reference_number?: string;
}

// カテゴリ選択用のAutocompleteコンポーネント
interface CategoryAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  accountType: AccountType;
  transactionType: TransactionType;
  required?: boolean;
  label?: string;
}

const CategoryAutocomplete: React.FC<CategoryAutocompleteProps> = ({
  value,
  onChange,
  accountType,
  transactionType,
  required = false,
  label = 'カテゴリ'
}) => {
  const { categories, loading } = useCategories({
    accountType,
    transactionType
  });

  return (
    <Autocomplete
      value={value || null}
      onChange={(_, newValue) => {
        // 新しい値が選択された場合（既存オプション or 新規入力）
        if (typeof newValue === 'string') {
          onChange(newValue);
        } else {
          onChange('');
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        // 入力中の値を更新（reason === 'input' の場合のみ）
        if (reason === 'input') {
          onChange(newInputValue);
        }
      }}
      options={categories}
      freeSolo
      loading={loading}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps}>
            {option}
          </li>
        );
      }}
    />
  );
};

const DataManagement: React.FC = () => {
  const { user, organization } = useAuth();
  const queryClient = useQueryClient();
  
  
  // 利用可能な年度を取得（実際のデータが存在する範囲）
  const { data: availableFiscalYears, isLoading: availableYearsLoading } = useAvailableFiscalYears();
  
  // 現在選択中の年度（デフォルトは最新年度）
  const [currentYear, setCurrentYear] = useState<number | null>(null);
  
  // 利用可能な年度が読み込まれたら、最新年度を初期値に設定
  React.useEffect(() => {
    if (availableFiscalYears && availableFiscalYears.length > 0 && currentYear === null) {
      const latestYear = Math.max(...availableFiscalYears);
      setCurrentYear(latestYear);
    }
  }, [availableFiscalYears, currentYear]);
  
  const availableYears = availableFiscalYears || [];
  
  // 取引データフェッチ
  const { data: transactions, isLoading: transactionsLoading, error: transactionsError } = useTransactions(
    currentYear ? { fiscalYear: currentYear } : undefined
  );

  // タブ管理
  const [tabValue, setTabValue] = useState(0);

  // ファイルアップロード関連
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [fileImportOpen, setFileImportOpen] = useState(false);
  const [importAccountType, setImportAccountType] = useState<'general' | 'reserve'>('general');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [accountType, setAccountType] = useState<'general' | 'reserve'>('general');
  const [uploadStatus, setUploadStatus] = useState<string>('');

  // 手入力フォーム関連
  const [manualInputOpen, setManualInputOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<TransactionForm>({
    account_type: 'general',
    type: 'income',
    amount: 0,
    category: '',
    transaction_date: new Date().toISOString().split('T')[0],
    description: '',
    reference_number: ''
  });
  
  // 編集・削除関連
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  
  // ページネーション関連
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // フィルタリング関連
  const [filterAccountType, setFilterAccountType] = useState<AccountType | 'all'>('all');
  const [filterType, setFilterType] = useState<TransactionType | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('');
  
  // 編集フォームの初期状態
  const getDefaultEditFormData = (): TransactionForm => ({
    account_type: 'general',
    type: 'income',
    amount: 0,
    category: '',
    transaction_date: new Date().toISOString().split('T')[0],
    description: '',
    reference_number: ''
  });
  
  const [editFormData, setEditFormData] = useState<TransactionForm>(getDefaultEditFormData());

  // 将来の実装用（現在はコメントアウト）
  /*
  const getStatusColor = (status: ImportHistory['status']) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: ImportHistory['status']) => {
    switch (status) {
      case 'success': return '成功';
      case 'error': return 'エラー';
      case 'warning': return '警告';
      default: return '不明';
    }
  };
  */

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // 新しいファイルインポート機能
  const handleFileImport = (accountType: 'general' | 'reserve') => {
    setImportAccountType(accountType);
    setFileImportOpen(true);
  };

  const handleImportSuccess = (result: any) => {
    setFileImportOpen(false);
    // 成功メッセージや結果表示は FileImport コンポーネント内で処理
  };

  // 旧アップロード機能（デモ用）
  const handleUpload = () => {
    if (selectedFile) {
      setUploadStatus(`${selectedFile.name} をアップロードしています...`);
      
      setTimeout(() => {
        setUploadStatus('アップロードが完了しました！');
        setSelectedFile(null);
        setUploadDialogOpen(false);
      }, 2000);
    }
  };

  // 将来の実装用（現在はコメントアウト）
  /*
  const handleExport = (type: 'excel' | 'pdf', accountType: 'general' | 'reserve') => {
    alert(`${accountType === 'general' ? '一般会計' : '積立会計'}データを${type.toUpperCase()}形式でエクスポートします`);
  };
  */

  // 手入力関数
  const handleFormChange = (field: keyof TransactionForm, value: string | number) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // 会計種別または取引種別が変更された場合、カテゴリをリセット
      if (field === 'account_type' || field === 'type') {
        newData.category = '';
      }
      
      return newData;
    });
  };

  const handleManualSubmit = async () => {
    if (!user || !organization) return;

    setIsSubmitting(true);
    try {
      // 新しいモジュール化されたサービスを使用
      const ingestionService = new TransactionIngestionService(organization.id, user.id);
      
      // 手動入力データを正規化
      const normalizedTransaction = normalizeManualInput(formData);
      
      // データ取り込み実行
      const result = await ingestionService.ingestSingleTransaction(normalizedTransaction);
      
      // 結果の処理
      if (result.summary.errorCount > 0) {
        const firstError = result.errors[0];
        throw new Error(firstError.error);
      }
      
      if (result.summary.duplicateCount > 0) {
        alert('この取引は既に登録済みです（重複データ）');
        return;
      }
      
      if (result.summary.successCount > 0) {
        // 成功時の処理
        
        // フォームリセット
        setFormData({
          account_type: 'general',
          type: 'income',
          amount: 0,
          category: '',
          transaction_date: new Date().toISOString().split('T')[0],
          description: '',
          reference_number: ''
        });

        setManualInputOpen(false);
        
        // 全ての取引関連のクエリを無効化して再フェッチ
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
        queryClient.invalidateQueries({ queryKey: ['monthly-data'] });
        queryClient.invalidateQueries({ queryKey: ['yearly-data'] });
        queryClient.invalidateQueries({ queryKey: ['account-balances'] });
      }
      
    } catch (error) {
      console.error('取引追加エラー:', error);
      
      // エラーの詳細情報を表示
      if (error instanceof Error) {
        alert(`取引の追加に失敗しました: ${error.message}`);
      } else {
        alert('取引の追加に失敗しました。詳細はコンソールを確認してください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // 編集ダイアログを開く
  const handleEditTransaction = (transaction: Transaction) => {
    
    setSelectedTransaction(transaction);
    
    // フォームデータを安全に設定（型チェック付き）
    const safeEditFormData: TransactionForm = {
      account_type: ['general', 'reserve'].includes(transaction.account_type) 
        ? transaction.account_type as AccountType 
        : 'general',
      type: ['income', 'expense'].includes(transaction.type) 
        ? transaction.type as TransactionType 
        : 'income',
      amount: typeof transaction.amount === 'number' ? transaction.amount : 0,
      category: typeof transaction.category === 'string' ? transaction.category : '',
      transaction_date: typeof transaction.transaction_date === 'string' 
        ? transaction.transaction_date 
        : new Date().toISOString().split('T')[0],
      description: typeof transaction.description === 'string' 
        ? transaction.description || '' 
        : '',
      reference_number: typeof transaction.reference_number === 'string' 
        ? transaction.reference_number || '' 
        : ''
    };
    setEditFormData(safeEditFormData);
    setEditDialogOpen(true);
  };
  
  // 編集内容を保存
  const handleEditSubmit = async () => {
    if (!selectedTransaction || !user || !organization) return;
    
    setIsSubmitting(true);
    try {
      
      const updateData = {
        account_type: editFormData.account_type,
        type: editFormData.type,
        amount: editFormData.amount,
        category: editFormData.category,
        transaction_date: editFormData.transaction_date,
        description: editFormData.description || null,
        reference_number: editFormData.reference_number || null
      };
      
      const { error } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', selectedTransaction.id)
        .select();
      
      if (error) {
        console.error('編集エラー:', error);
        throw error;
      }
      
      setEditDialogOpen(false);
      setSelectedTransaction(null);
      setEditFormData(getDefaultEditFormData());
      
      // キャッシュを無効化
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-data'] });
      queryClient.invalidateQueries({ queryKey: ['yearly-data'] });
      queryClient.invalidateQueries({ queryKey: ['account-balances'] });
    } catch (error) {
      console.error('取引更新エラー詳細:', error);
      if (error instanceof Error) {
        alert(`取引の更新に失敗しました: ${error.message}`);
      } else {
        alert('取引の更新に失敗しました。詳細はコンソールを確認してください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // 削除ダイアログを開く
  const handleDeleteTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDeleteDialogOpen(true);
  };
  
  // 取引を削除
  const handleDeleteConfirm = async () => {
    if (!selectedTransaction) return;
    
    try {
      
      // 現在の認証状態を確認
      await supabase.auth.getSession();
      
      // Supabaseクライアント設定確認
      
      // 削除前にレコードが存在するか確認
      const { data: existingData, error: selectError } = await supabase
        .from('transactions')
        .select('id')
        .eq('id', selectedTransaction.id);
      
      if (selectError) {
        throw new Error(`削除対象が見つかりません: ${selectError.message}`);
      }
      
      if (!existingData || existingData.length === 0) {
        throw new Error('削除対象の取引が見つかりません');
      }
      
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', selectedTransaction.id)
        .select();
      
      if (error) {
        console.error('Supabase削除エラー:', error);
        throw error;
      }
      
      setDeleteDialogOpen(false);
      setSelectedTransaction(null);
      
      // キャッシュを無効化
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-data'] });
      queryClient.invalidateQueries({ queryKey: ['yearly-data'] });
      queryClient.invalidateQueries({ queryKey: ['account-balances'] });
    } catch (error) {
      console.error('取引削除エラー詳細:', error);
      if (error instanceof Error) {
        alert(`取引の削除に失敗しました: ${error.message}`);
      } else {
        alert('取引の削除に失敗しました。詳細はコンソールを確認してください。');
      }
    }
  };
  
  // 編集フォームの値変更
  const handleEditFormChange = (field: keyof TransactionForm, value: string | number) => {
    setEditFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // 会計種別または取引種別が変更された場合、カテゴリをリセット
      if (field === 'account_type' || field === 'type') {
        newData.category = '';
      }
      
      return newData;
    });
  };
  
  // 通貨フォーマット
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(amount);
  };
  
  // 取引タイプの色を取得
  const getTransactionColor = (type: TransactionType) => {
    return type === 'income' ? 'success' : 'error';
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        データ管理
      </Typography>

      {/* タブ */}
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="手入力・インポート" />
        <Tab label="エクスポート・履歴" />
      </Tabs>

      {/* 手入力・インポートタブ */}
      {tabValue === 0 && (
        <Box>
          {/* 手入力・ファイルアップロード */}
          <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    手動入力
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    取引データを手動で入力します
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    fullWidth
                    onClick={() => setManualInputOpen(true)}
                    color="primary"
                  >
                    新しい取引を追加
                  </Button>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 300px' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ファイルインポート
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Excel または CSV ファイルから収支データを取り込みます
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                    <Button
                      variant="contained"
                      startIcon={<Upload />}
                      onClick={() => handleFileImport('general')}
                      color="primary"
                    >
                      一般会計データをインポート
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Upload />}
                      onClick={() => handleFileImport('reserve')}
                      color="secondary"
                    >
                      積立会計データをインポート
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
          
          {/* 取引履歴表示 */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                取引履歴 ({currentYear}年度)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {availableYears.map(year => (
                  <Button
                    key={year}
                    variant="outlined"
                    size="small"
                    onClick={() => setCurrentYear(year)}
                    color={currentYear === year ? 'primary' : 'inherit'}
                  >
                    {year}年度
                  </Button>
                ))}
              </Box>
            </Box>
            
            {/* サマリー統計 */}
            {transactions && (() => {
              // フィルタ後の統計
              let filteredTransactions = transactions;
              
              if (filterAccountType !== 'all') {
                filteredTransactions = filteredTransactions.filter(
                  t => t.account_type === filterAccountType
                );
              }
              
              if (filterType !== 'all') {
                filteredTransactions = filteredTransactions.filter(
                  t => t.type === filterType
                );
              }
              
              if (filterCategory.trim()) {
                filteredTransactions = filteredTransactions.filter(
                  t => t.category?.toLowerCase().includes(filterCategory.toLowerCase())
                );
              }
              
              const stats = {
                totalIncome: filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0),
                totalExpense: filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0),
                generalCount: filteredTransactions.filter(t => t.account_type === 'general').length,
                reserveCount: filteredTransactions.filter(t => t.account_type === 'reserve').length
              };
              
              return (stats.totalIncome > 0 || stats.totalExpense > 0) ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="h6" color="success.contrastText">{formatCurrency(stats.totalIncome)}</Typography>
                    <Typography variant="body2" color="success.contrastText">収入合計</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Typography variant="h6" color="error.contrastText">{formatCurrency(stats.totalExpense)}</Typography>
                    <Typography variant="body2" color="error.contrastText">支出合計</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="h6" color="info.contrastText">{formatCurrency(stats.totalIncome - stats.totalExpense)}</Typography>
                    <Typography variant="body2" color="info.contrastText">収支差額</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.200', borderRadius: 1 }}>
                    <Typography variant="h6">{stats.generalCount}</Typography>
                    <Typography variant="body2">一般会計</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.300', borderRadius: 1 }}>
                    <Typography variant="h6">{stats.reserveCount}</Typography>
                    <Typography variant="body2">積立会計</Typography>
                  </Box>
                </Box>
              ) : null;
            })()}
            
            {transactionsLoading || availableYearsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : transactionsError ? (
              <Alert severity="error">
                取引データの取得中にエラーが発生しました: {transactionsError.message}
              </Alert>
            ) : (
              <>
              {/* フィルタリング・ページネーション設定 */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>会計種別</InputLabel>
                  <Select
                    value={filterAccountType}
                    onChange={(e) => {
                      setFilterAccountType(e.target.value as AccountType | 'all');
                      setCurrentPage(1);
                    }}
                    label="会計種別"
                  >
                    <MenuItem value="all">全て</MenuItem>
                    <MenuItem value="general">一般会計</MenuItem>
                    <MenuItem value="reserve">積立会計</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>取引種別</InputLabel>
                  <Select
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value as TransactionType | 'all');
                      setCurrentPage(1);
                    }}
                    label="取引種別"
                  >
                    <MenuItem value="all">全て</MenuItem>
                    <MenuItem value="income">収入</MenuItem>
                    <MenuItem value="expense">支出</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  size="small"
                  label="カテゴリ検索"
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="例：管理費、修繕費"
                  sx={{ minWidth: 150 }}
                />
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>表示件数</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    label="表示件数"
                  >
                    <MenuItem value={10}>10件</MenuItem>
                    <MenuItem value={20}>20件</MenuItem>
                    <MenuItem value={50}>50件</MenuItem>
                    <MenuItem value={100}>100件</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>取引日</TableCell>
                      <TableCell>会計種別</TableCell>
                      <TableCell>種別</TableCell>
                      <TableCell>カテゴリ</TableCell>
                      <TableCell align="right">金額</TableCell>
                      <TableCell>操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      if (!transactions) return [];
                      
                      // フィルタリング
                      let filteredTransactions = transactions;
                      
                      if (filterAccountType !== 'all') {
                        filteredTransactions = filteredTransactions.filter(
                          t => t.account_type === filterAccountType
                        );
                      }
                      
                      if (filterType !== 'all') {
                        filteredTransactions = filteredTransactions.filter(
                          t => t.type === filterType
                        );
                      }
                      
                      if (filterCategory.trim()) {
                        filteredTransactions = filteredTransactions.filter(
                          t => t.category?.toLowerCase().includes(filterCategory.toLowerCase())
                        );
                      }
                      
                      // ページネーション
                      const startIndex = (currentPage - 1) * pageSize;
                      const endIndex = startIndex + pageSize;
                      
                      return filteredTransactions.slice(startIndex, endIndex).map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{transaction.transaction_date}</TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.account_type === 'general' ? '一般会計' : '積立会計'}
                            size="small"
                            color={transaction.account_type === 'general' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.type === 'income' ? '収入' : '支出'}
                            size="small"
                            color={getTransactionColor(transaction.type)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={transaction.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={getTransactionColor(transaction.type)}
                            fontWeight={transaction.type === 'income' ? 'bold' : 'normal'}
                          >
                            {formatCurrency(transaction.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditTransaction(transaction)}
                              color="primary"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteTransaction(transaction)}
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}
            
            {transactions && (() => {
              // フィルタ後の件数を計算
              let filteredTransactions = transactions;
              
              if (filterAccountType !== 'all') {
                filteredTransactions = filteredTransactions.filter(
                  t => t.account_type === filterAccountType
                );
              }
              
              if (filterType !== 'all') {
                filteredTransactions = filteredTransactions.filter(
                  t => t.type === filterType
                );
              }
              
              if (filterCategory.trim()) {
                filteredTransactions = filteredTransactions.filter(
                  t => t.category?.toLowerCase().includes(filterCategory.toLowerCase())
                );
              }
              
              const totalPages = Math.ceil(filteredTransactions.length / pageSize);
              
              return (
                <Box>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      {currentYear}年度：{filteredTransactions.length}件の取引
                      {filteredTransactions.length !== transactions.length && 
                        ` （全${transactions.length}件中）`
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ページ {currentPage} / {totalPages}
                    </Typography>
                  </Box>
                  
                  {totalPages > 1 && (
                    <Stack spacing={2} sx={{ mt: 2 }} alignItems="center">
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Stack>
                  )}
                </Box>
              );
            })()}
          </Paper>
        </Box>
      )}

      {/* エクスポート・履歴タブ */}
      {tabValue === 1 && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Paper sx={{ p: 6, maxWidth: 600, width: '100%' }}>
            <Construction 
              sx={{ 
                fontSize: 80, 
                color: 'primary.main', 
                mb: 3 
              }} 
            />
            
            <Typography variant="h5" gutterBottom color="primary">
              エクスポート・履歴機能
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              今後実装予定
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
              データのエクスポートやインポート履歴管理、<br />
              データ品質チェック機能を<br />
              今後のバージョンで提供予定です。
            </Typography>
            
            <Alert severity="info" sx={{ textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>予定機能:</strong><br />
                • Excel/CSV形式でのデータエクスポート<br />
                • インポート履歴の管理・追跡<br />
                • データ品質チェック・検証<br />
                • バックアップ・復元機能<br />
                • データ整合性の監視
              </Typography>
            </Alert>
          </Paper>
        </Box>
      )}

      {/* 
      将来の実装用（エクスポート・履歴機能のコメントアウト）
      */}
      {/*
      {tabValue === 1 && (
        <Box>
          <Box sx={{ mb: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  データエクスポート
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  現在のデータを Excel または PDF 形式で出力します
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" startIcon={<Download />}>
                    一般会計 Excel
                  </Button>
                  <Button variant="outlined" startIcon={<Download />}>
                    積立会計 Excel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
      */}

      {/* 手入力ダイアログ */}
      <Dialog open={manualInputOpen} onClose={() => setManualInputOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>新しい取引を追加</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>会計種別</InputLabel>
                <Select
                  value={formData.account_type}
                  onChange={(e) => handleFormChange('account_type', e.target.value)}
                  label="会計種別"
                >
                  <MenuItem value="general">一般会計</MenuItem>
                  <MenuItem value="reserve">積立会計</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>取引種別</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  label="取引種別"
                >
                  <MenuItem value="income">収入</MenuItem>
                  <MenuItem value="expense">支出</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="金額"
                type="number"
                value={formData.amount}
                onChange={(e) => handleFormChange('amount', parseInt(e.target.value) || 0)}
                sx={{ flex: 1 }}
                required
              />
              <TextField
                label="取引日"
                type="date"
                value={formData.transaction_date}
                onChange={(e) => handleFormChange('transaction_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
                required
              />
            </Box>

            <CategoryAutocomplete
              value={formData.category}
              onChange={(value) => handleFormChange('category', value)}
              accountType={formData.account_type}
              transactionType={formData.type}
              required
            />


            <TextField
              label="摘要（任意）"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="取引の詳細な内容説明"
            />

            <TextField
              label="メモ（任意）"
              value={formData.reference_number}
              onChange={(e) => handleFormChange('reference_number', e.target.value)}
              placeholder="取引に関するメモや備考"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualInputOpen(false)}>キャンセル</Button>
          <Button 
            onClick={handleManualSubmit} 
            variant="contained"
            disabled={isSubmitting || !formData.amount || !formData.category}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting ? '追加中...' : '取引を追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>取引を編集</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>会計種別</InputLabel>
                <Select
                  value={editFormData.account_type}
                  onChange={(e) => handleEditFormChange('account_type', e.target.value)}
                  label="会計種別"
                >
                  <MenuItem value="general">一般会計</MenuItem>
                  <MenuItem value="reserve">積立会計</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>取引種別</InputLabel>
                <Select
                  value={editFormData.type}
                  onChange={(e) => handleEditFormChange('type', e.target.value)}
                  label="取引種別"
                >
                  <MenuItem value="income">収入</MenuItem>
                  <MenuItem value="expense">支出</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="金額"
                type="number"
                value={editFormData.amount}
                onChange={(e) => handleEditFormChange('amount', parseInt(e.target.value) || 0)}
                sx={{ flex: 1 }}
                required
              />
              <TextField
                label="取引日"
                type="date"
                value={editFormData.transaction_date}
                onChange={(e) => handleEditFormChange('transaction_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
                required
              />
            </Box>

            <CategoryAutocomplete
              value={editFormData.category}
              onChange={(value) => handleEditFormChange('category', value)}
              accountType={editFormData.account_type}
              transactionType={editFormData.type}
              required
            />


            <TextField
              label="摘要（任意）"
              value={editFormData.description}
              onChange={(e) => handleEditFormChange('description', e.target.value)}
              placeholder="取引の詳細な内容説明"
            />

            <TextField
              label="メモ（任意）"
              value={editFormData.reference_number}
              onChange={(e) => handleEditFormChange('reference_number', e.target.value)}
              placeholder="取引に関するメモや備考"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setSelectedTransaction(null);
            setEditFormData(getDefaultEditFormData());
          }}>キャンセル</Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained"
            disabled={isSubmitting || !editFormData.amount || !editFormData.category}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting ? '更新中...' : '更新'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
        <DialogTitle>取引を削除</DialogTitle>
        <DialogContent>
          <Typography>
            以下の取引を削除しますか？この操作は取り消すことができません。
          </Typography>
          {selectedTransaction && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">取引日</Typography>
              <Typography variant="body1">{selectedTransaction.transaction_date}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>金額</Typography>
              <Typography variant="h6" color={getTransactionColor(selectedTransaction.type)}>
                {formatCurrency(selectedTransaction.amount)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* ファイルインポートダイアログ */}
      {fileImportOpen && (
        <FileImport
          accountType={importAccountType}
          onClose={() => setFileImportOpen(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      {/* アップロードダイアログ */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ファイルアップロード</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>会計種別</InputLabel>
              <Select
                value={accountType}
                onChange={(e) => setAccountType(e.target.value as 'general' | 'reserve')}
                label="会計種別"
              >
                <MenuItem value="general">一般会計</MenuItem>
                <MenuItem value="reserve">積立会計</MenuItem>
              </Select>
            </FormControl>

            <input
              type="file"
              accept=".xlsx,.xls,.pdf"
              onChange={handleFileSelect}
              style={{ marginBottom: '16px' }}
            />

            {selectedFile && (
              <Alert severity="info" sx={{ mb: 2 }}>
                選択されたファイル: {selectedFile.name}
              </Alert>
            )}

            {uploadStatus && (
              <Alert severity="success">
                {uploadStatus}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!selectedFile}>
            アップロード
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DataManagement;
